

































































































import { computed, defineComponent, ref } from "@vue/composition-api";
import { debounce } from "lodash-es";
import { Vue } from "vue/types/vue";
import { Location } from "vue-router";
import { CallOptions } from "@scrile/api-provider/dist/api/CallOptionsProvider";
import { Message } from "@scrile/api-provider/dist/api/MessagesProvider";
import { MessageThread } from "@scrile/api-provider/dist/api/MessageThreadsProvider";
import { PaymentPackage } from "@scrile/api-provider/dist/api/PaymentPackageProvider";
import { Subscription } from "@scrile/api-provider/dist/api/SubscriptionProvider";
import { UserPublicData } from "@scrile/api-provider/dist/projects/scrileConnect/UserPublicDataProvider";
import { checkAuthorities, checkRole } from "@scrile/tools/dist/lib/SecurityHelpers";
import useAdminManagedUser from "@/hooks/useAdminManagedUser";
import useAuthUser from "@/hooks/useAuthUser";
import useKeepCustomScrollPosition from "@/hooks/useKeepCustomScrollPosition";
import emitter from "@/lib/emitter";
import { $t } from "@/lib/i18nHelpers";
import { getProfileUrl } from "@/lib/urlHelpers";
import { isGetUserMediaSupported, isWebRTCSupported } from "@/lib/webrtcHelpers";
import config from "@/config";
import { BaseMenuItem, BlockingStatus } from "@/types";
import { EVENTS } from "@/const";
import BaseAvatar from "@/components/BaseAvatar";
import BaseIcon from "@/components/BaseIcon";
import BaseIconButton from "@/components/BaseIconButton";
import BaseList, { BaseListItem } from "@/components/BaseList";
import BaseMenu from "@/components/BaseMenu";
import BasePage from "@/components/BasePage";
import BlockUserController from "@/components/BlockUserController";
import CustomScroll from "@/components/CustomScroll";
import MessageSendWithAttachment from "@/components/MessageSendWithAttachment";
import PageTitle from "@/components/PageTitle";
import MessageDeleteConfirm from "@/views/PageMessages/components/MessageDeleteConfirm.vue";
import SubscriberIcon from "@/views/PageMessageThreads/components/SubscriberIcon.vue";
import GroupModal from "./GroupModal.vue";
import MessageList from "./MessageList.vue";
import MessageSystemNote from "./MessageSystemNote.vue";
import SubscriptionOffer from "./SubscriptionOffer";

export default defineComponent({
  name: "MessagesView",
  components: {
    SubscriptionOffer,
    MessageDeleteConfirm,
    MessageList,
    BasePage,
    PageTitle,
    BaseAvatar,
    MessageSendWithAttachment,
    BaseIcon,
    BlockUserController,
    BaseIconButton,
    BaseList,
    BaseListItem,
    BaseMenu,
    MessageSystemNote,
    GroupModal,
    CustomScroll,
    SubscriberIcon,
  },
  props: {
    loading: { type: Boolean, required: true },
    allowResponse: { type: Boolean, required: true },
    allowManageMessages: { type: Boolean, required: true },
    authUserId: { type: String, required: true },
    messageThread: { type: Object as () => MessageThread | null },
    messages: { type: Array as () => Message[], required: true },
    minUnreadMessageId: { type: String, required: true },
    enableScroll: { type: Boolean, default: false },
    user: { type: Object as () => UserPublicData | null },
    userCallOptions: { type: Object as () => CallOptions | null },
    opponentSubscription: { type: Object as () => Subscription | null },
    paymentPackages: { type: Array as () => PaymentPackage[] | null },
  },
  setup(props, { emit }) {
    const { authUser, authorities, isCreator: isAuthUserCreator } = useAuthUser();
    const { isAdminManagedUser: chatGalleryAllowed } = useAdminManagedUser();
    const list = ref<Vue | undefined>();
    const { scrollNode, scrollToBottom } = useKeepCustomScrollPosition(list, { keepScroll: true, behavior: "smooth" });
    const blockingStatus = ref<BlockingStatus>({ authUser: false, opponent: false });
    const showBlockingConfirm = ref(false);
    const showGroupModal = ref(false);
    const messageToDelete = ref<Message | null>(null);

    const screenName = computed(() => props.user?.screenName ?? "");
    const pinned = computed(() => !!props.messageThread?.pinTime);
    const titleComponent = computed(() => (props.user ? "router-link" : "div"));
    const profileTo = computed<Location | undefined>(() => (props.user ? getProfileUrl(props.user) : undefined));
    const isCallable = computed(
      () =>
        config.featureCalling && !!props.user && props.user.id !== props.authUserId && !!props.userCallOptions?.enabled
    );
    const userId = computed(() => props.user?.id ?? "");
    const isChatBlocked = computed(() => blockingStatus.value.opponent || blockingStatus.value.authUser);
    const isMessageSendShow = computed(() => !!props.user && !isChatBlocked.value && props.allowResponse);
    const threadId = computed(() => (props.messageThread ? props.messageThread.id : undefined));
    const isOpponentAdmin = computed(() => checkRole(props.user?.role ?? "", "admin"));

    const canDeleteMessageForAll = computed(() => {
      const isOpponentMessage = !!messageToDelete.value && messageToDelete.value.senderId !== authUser.value?.id;
      const isPurchasedMessage =
        !!messageToDelete.value &&
        !!props.user &&
        !!messageToDelete.value.attachment &&
        messageToDelete.value.attachmentPurchasers?.includes(props.user.id);
      const canDeleteAnyMessages = checkAuthorities(authorities.value, ["deleteAnyMessage"]);
      return isOpponentMessage ? canDeleteAnyMessages : !isPurchasedMessage;
    });

    const allowManageLists = computed(() => checkAuthorities(authorities.value, ["manageMyGroups"]));

    const menuItems = computed<BaseMenuItem[]>(() => {
      const menuItems = [];
      if (props.messageThread && !isChatBlocked.value) {
        menuItems.push({
          name: "pin-user",
          text: !pinned.value ? $t("Mark as important") : $t("Unmark as important"),
          prependIcon: "sci-star",
          ...(pinned.value
            ? {
                prependIconColor: "var(--palette-primary)",
                class: "__active",
              }
            : {}),
        });
        if (isAuthUserCreator.value) {
          if (allowManageLists.value) {
            menuItems.push({
              name: "add-to-list",
              class: "messages__list-icon __add-to-group",
              text: $t("Add to list"),
              prependIcon: "sci-plus",
            });
          }
          if (!isOpponentAdmin.value) {
            menuItems.push({
              name: "block-user",
              text: $t(blockingStatus.value.opponent ? $t("Unblock user") : $t("Block user")),
              prependIcon: "sci-blocked",
              prependIconColor: "var(--palette-negative)",
            });
          }
        }
      }

      return menuItems;
    });

    const noteText = computed(() =>
      blockingStatus.value.opponent
        ? $t("You blocked {0}.", [props.user?.screenName])
        : $t("{0} blocked you. You can’t send messages to this creator anymore.", [props.user?.screenName])
    );

    const isWebRTCSupport = computed(() => {
      return isWebRTCSupported() && isGetUserMediaSupported();
    });
    const handleCall = debounce(() => {
      if (isWebRTCSupport.value) {
        emitter.emit(EVENTS.CALL_OUTGOING, { opponentUser: props.user });
      } else {
        emitter.emit(EVENTS.NOTIFICATION_SHOW, { text: $t("This browser does not support WebRTC") });
      }
    }, 300);

    const addMessage = (message: Message) => {
      emit("addMessage", message);
      scrollToBottom();
    };
    const receivedMessage = () => {
      if (!scrollNode.value) return;
      const bottomScrollTopPosition = scrollNode.value.scrollHeight - scrollNode.value.offsetHeight;
      if (
        bottomScrollTopPosition - scrollNode.value.scrollTop > 0 &&
        bottomScrollTopPosition - scrollNode.value.scrollTop < 100
      ) {
        scrollToBottom();
      }
    };
    const closeDeleteConfirm = () => {
      messageToDelete.value = null;
    };
    const handleDeleted = () => {
      if (messageToDelete.value) {
        emit("deleted", messageToDelete.value.id);
        closeDeleteConfirm();
      }
    };
    const toggleShowGroupModal = () => {
      showGroupModal.value = !showGroupModal.value;
    };

    return {
      blockingStatus,
      list,
      scrollNode,
      showBlockingConfirm,
      allowManageLists,
      isMessageSendShow,
      isCallable,
      profileTo,
      userId,
      threadId,
      screenName,
      pinned,
      titleComponent,
      messageToDelete,
      isChatBlocked,
      menuItems,
      noteText,
      showGroupModal,
      canDeleteMessageForAll,
      chatGalleryAllowed,
      handleCall,
      scrollToBottom,
      addMessage,
      receivedMessage,
      handleDeleted,
      toggleShowGroupModal,
      closeDeleteConfirm,
    };
  },
  created() {
    this.$on("receivedMessage", this.receivedMessage);
  },
  beforeDestroy() {
    this.$off("receivedMessage", this.receivedMessage);
  },
});
